import { routes } from '../assessment/routes';

/**
 * Navigation model for the shared shell (V2-018C-1).
 *
 * Labels and destinations only. No marketing copy, no calls to action, and no
 * page content: this module exists so the desktop and mobile navigations render
 * one list rather than two that can drift apart.
 */
export type NavItem = {
  readonly label: string;
  readonly href: string;
};

/**
 * The assessments group. Presented as a disclosure on desktop and as a flat
 * labelled group on mobile — one level of interaction there, never two
 * (specification section 3.2).
 */
export const ASSESSMENT_NAV: readonly NavItem[] = [
  { label: 'Bottleneck Assessment', href: routes.bottleneck },
  { label: 'Work Fit Assessment', href: routes.workFit },
  { label: 'Perspective Assessment', href: routes.perspective },
];

/** Top-level destinations that are not part of the assessments group. */
export const PRIMARY_NAV: readonly NavItem[] = [{ label: 'Why HPOS', href: routes.why }];

/**
 * Where a signed-in visitor returns to from a public page.
 *
 * **PO-12 is answered (U1a): the dashboard.** This slot previously held the
 * report library, because that was the only delivered surface that qualified —
 * the legacy destination was a dashboard this product did not have. It does
 * now, so the slot points at the authenticated home rather than at one of the
 * surfaces inside it, and the label moved with it: "Your reports" described a
 * page, and this is no longer that page.
 *
 * The prediction recorded here held. Changing PO-12 took one line and no test
 * edit, because every assertion reads this constant instead of its value.
 */
export const ACCOUNT_DESTINATION: NavItem = {
  label: 'Dashboard',
  href: routes.dashboard,
};

/**
 * Navigation for the Application Shell (U1a).
 *
 * The Application Shell carried no navigation at all before this: its header
 * was a brand mark and an auth control, so a signed-in visitor's only route
 * between their own surfaces was the browser's back button. These are the
 * destinations that exist today; U1b adds nothing here, because its work is
 * content inside `/dashboard` rather than new destinations.
 *
 * Every entry must be a route the application actually serves. The Experience
 * Shell learned this the harder way — see the footer note in `SiteFooter` — and
 * `shellRoutes.test.ts` asserts it here rather than trusting the list.
 *
 * No admin entry. A4a adds one conditionally, behind the `/api/me/admin` check:
 * a statically rendered admin link would disclose to every signed-in user that
 * an administrative console exists.
 */
export const AUTHENTICATED_NAV: readonly NavItem[] = [
  { label: 'Dashboard', href: routes.dashboard },
  { label: 'Your reports', href: routes.reports },
];

/**
 * The administrative entry point.
 *
 * **Production's entry is a button on the customer dashboard**, labelled
 * `Admin Panel` (spec §1). V2 carried it as an `Admin` item in the application
 * shell's header instead, which put an administrative destination on every
 * authenticated page; the migration moves it to the one place production offers
 * it, and takes the label with it.
 *
 * Deliberately **not** part of `AUTHENTICATED_NAV`, for the reason A4a
 * established and the move does not change: it is rendered only after the
 * server has confirmed the caller is an administrator, because a statically
 * rendered entry would tell every signed-in user that an administrative console
 * exists and invite them to knock on it. Separating the constant is what makes
 * that conditional rendering the obvious thing to do rather than something a
 * later edit can quietly undo by appending to the shared list.
 */
export const ADMIN_ENTRY: NavItem = {
  label: 'Admin Panel',
  href: routes.admin,
};

/**
 * Support address, carried in the footer.
 *
 * Ported unchanged from the legacy footer and already present in the migrated
 * Terms of Service, so this introduces no new commitment.
 */
export const SUPPORT_EMAIL = 'support@highperformeros.com';

/**
 * The site-wide safety statement.
 *
 * Ported verbatim from the legacy footer, where it appears on every public page.
 * It is a standing safety commitment rather than marketing copy, and the legacy
 * claims reconciliation records its disposition as "port unchanged". It is the
 * only user-visible sentence this slice introduces.
 *
 * Specification section 3.3 requires it on every page of both shells, exactly
 * once per page.
 */
export const SAFETY_DISCLAIMER =
  'High Performer OS assessments are for personal development and coaching purposes only. ' +
  'They are not medical, psychological, or clinical advice.';

/**
 * The variant the legacy assessment pages carry.
 *
 * The legacy footer disclaimer is not uniform: `/` and `/why` speak about the
 * assessments collectively, while `/bottleneck` and `/work-fit` speak about the
 * one assessment the page is describing. Migration preserves both wordings
 * rather than flattening them to whichever came first.
 */
export const ASSESSMENT_SAFETY_DISCLAIMER =
  'This assessment is for personal development and coaching purposes only. ' +
  'It is not medical, psychological, or clinical advice.';

/**
 * Which disclaimer a given path carries.
 *
 * The legacy footer disclaimer was not uniform: `/` and `/why` used the
 * collective wording, `/bottleneck` and `/work-fit` used the assessment
 * wording, and `/perspective` carried **no footer disclaimer at all**.
 * Migration reproduced all three behaviours exactly and recorded the
 * `/perspective` omission as product debt.
 *
 * Launch readiness (Slice E) closes that debt. `/perspective` now carries the
 * same assessment wording as the other two assessment pages, so every public
 * page states the non-clinical position. This is a **deliberate departure from
 * legacy**: legacy had no disclaimer here to restore. The wording itself is
 * unchanged legacy text, taken from the sibling assessment pages rather than
 * written for this slice.
 *
 * The return type stays nullable. No path returns `null` today, but narrowing
 * it would mean editing the footer's render branch, which is outside this
 * slice.
 */
export function disclaimerForPath(path: string): string | null {
  const normalised = path.length > 1 ? path.replace(/\/+$/, '') : path;
  if (
    normalised === routes.bottleneck ||
    normalised === routes.workFit ||
    normalised === routes.perspective
  ) {
    return ASSESSMENT_SAFETY_DISCLAIMER;
  }
  return SAFETY_DISCLAIMER;
}
